import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Megaphone, Sparkles, Download, Copy, Check, Trash2, FileText, Target, Ban, DollarSign, MousePointer, TrendingUp, ChevronRight, Plus, Rocket, ExternalLink } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useProjects } from "@/context/ProjectContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function GoogleAdsPage() {
  const { active } = useProjects();
  const [url, setUrl] = useState("");
  const [seed, setSeed] = useState("");
  const [location, setLocation] = useState("United States");
  const [budget, setBudget] = useState(20);
  const [generated, setGenerated] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [savedCampaigns, setSavedCampaigns] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [campaignName, setCampaignName] = useState("");

  useEffect(() => {
    if (active?.domain && !url) {
      try {
        const u = active.domain.startsWith("http") ? active.domain : `https://${active.domain}`;
        setUrl(u);
      } catch {}
    }
  }, [active?.domain, url]);

  const loadCampaigns = async () => {
    try {
      const { data } = await api.get("/google-ads/campaigns");
      setSavedCampaigns(data);
    } catch {}
  };
  useEffect(() => { loadCampaigns(); }, []);

  const generate = async () => {
    if (!url && !seed) {
      toast.error("Provide a landing URL or seed keyword first.");
      return;
    }
    setGenerating(true);
    setGenerated(null);
    try {
      const { data } = await api.post("/google-ads/generate", {
        url: url || undefined,
        seed_keyword: seed || undefined,
        location,
        daily_budget_usd: Number(budget),
        project_id: active?.id,
      });
      setGenerated(data);
      toast.success(`${data.ads.length} ads + ${data.keyword_groups.length} keyword groups generated`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const saveCampaign = async () => {
    if (!campaignName) return;
    try {
      await api.post("/google-ads/campaigns", {
        name: campaignName,
        project_id: active?.id,
        landing_url: url,
        seed_keyword: seed,
        location,
        daily_budget_usd: Number(budget),
        ads: generated.ads,
        keyword_groups: generated.keyword_groups,
        negative_keywords: generated.negative_keywords,
        recommended_budget_usd: generated.recommended_budget_usd,
        estimated_clicks_per_day: generated.estimated_clicks_per_day,
        estimated_cpc_usd: generated.estimated_cpc_usd,
      });
      toast.success("Campaign saved");
      setSaveOpen(false);
      setCampaignName("");
      loadCampaigns();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeCampaign = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await api.delete(`/google-ads/campaigns/${id}`);
      loadCampaigns();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout
      eyebrow="Paid"
      title="Google Ads Campaign Builder"
      subtitle="AI-built Responsive Search Ads + keyword groups + negatives, ready to paste into Google Ads."
    >
      {/* Builder form */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 mb-6" data-testid="gads-builder">
        <div className="grid lg:grid-cols-2 gap-4 mb-4">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Landing URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yoursite.com/product"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="gads-url" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Or seed keyword</Label>
            <Input value={seed} onChange={(e) => setSeed(e.target.value)} placeholder="ai seo tools"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="gads-seed" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Location</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)}
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="gads-location" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Daily budget (USD)</Label>
            <Input type="number" min={1} value={budget} onChange={(e) => setBudget(e.target.value)}
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="gads-budget" />
          </div>
        </div>
        <Button onClick={generate} disabled={generating} className="h-11 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="gads-generate-btn">
          {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building campaign…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate campaign</>}
        </Button>
      </div>

      {/* Results */}
      {generated && (
        <div className="space-y-6 fade-in" data-testid="gads-result">
          <KpiStrip data={generated} budget={budget} onSave={() => setSaveOpen(true)} />
          <Tabs defaultValue="ads">
            <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm">
              <TabsTrigger value="ads" className="data-[state=active]:bg-[#0055FF] data-[state=active]:text-white rounded-sm" data-testid="gads-tab-ads"><FileText className="w-4 h-4 mr-2" /> Ads ({generated.ads.length})</TabsTrigger>
              <TabsTrigger value="groups" className="data-[state=active]:bg-[#0055FF] data-[state=active]:text-white rounded-sm" data-testid="gads-tab-groups"><Target className="w-4 h-4 mr-2" /> Keyword groups ({generated.keyword_groups.length})</TabsTrigger>
              <TabsTrigger value="negatives" className="data-[state=active]:bg-[#0055FF] data-[state=active]:text-white rounded-sm" data-testid="gads-tab-neg"><Ban className="w-4 h-4 mr-2" /> Negatives ({generated.negative_keywords.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="ads" className="space-y-4 mt-5">
              {generated.ads.map((ad, i) => <AdCard key={i} ad={ad} idx={i} />)}
            </TabsContent>
            <TabsContent value="groups" className="space-y-4 mt-5">
              {generated.keyword_groups.map((g, i) => <GroupCard key={i} group={g} />)}
            </TabsContent>
            <TabsContent value="negatives" className="mt-5">
              <NegativeList negatives={generated.negative_keywords} />
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Saved campaigns */}
      {savedCampaigns.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-lg font-semibold tracking-tight mb-4">Saved campaigns</h2>
          <ul className="space-y-2" data-testid="gads-saved-list">
            {savedCampaigns.map((c) => (
              <li key={c.id} className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 flex items-center gap-4 flex-wrap" data-testid={`gads-saved-${c.id}`}>
                <div className="w-10 h-10 rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center"><Megaphone className="w-5 h-5 text-[#3377FF]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-xs text-zinc-500 truncate font-mono">{c.landing_url || c.seed_keyword} · ${c.daily_budget_usd}/day</div>
                </div>
                <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500 text-[10px]">{c.ads?.length || 0} ads · {c.keyword_groups?.length || 0} groups</Badge>
                {c.live_campaign_id ? (
                  <a href={`https://ads.google.com/aw/campaigns?campaignId=${c.live_campaign_id}`} target="_blank" rel="noreferrer" data-testid={`gads-live-link-${c.id}`}>
                    <Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30 rounded-sm gap-1 hover:bg-[#10B981]/25">
                      Live · PAUSED <ExternalLink className="w-3 h-3" />
                    </Badge>
                  </a>
                ) : (
                  <PushLiveButton campaignId={c.id} onPushed={loadCampaigns} />
                )}
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/google-ads/campaigns/${c.id}/export.csv`} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm" className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid={`gads-export-${c.id}`}>
                    <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                  </Button>
                </a>
                <Button variant="outline" size="sm" onClick={() => removeCampaign(c.id)} className="bg-zinc-950 hover:bg-red-950/40 border-zinc-800 hover:border-red-900/60 rounded-sm text-red-400" data-testid={`gads-delete-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!generated && savedCampaigns.length === 0 && (
        <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center text-zinc-500">
          <Megaphone className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
          <p>Enter a landing URL or seed keyword to build your first Google Ads campaign.</p>
        </div>
      )}

      {/* Save dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
          <DialogHeader>
            <DialogTitle>Save campaign</DialogTitle>
          </DialogHeader>
          <Input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="e.g. AI SEO Tools — Q1 2026"
                 className="bg-zinc-950 border-zinc-800 rounded-sm h-11" data-testid="gads-save-name" />
          <DialogFooter>
            <Button onClick={saveCampaign} disabled={!campaignName} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="gads-save-confirm">
              <Plus className="w-4 h-4 mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function KpiStrip({ data, budget, onSave }) {
  return (
    <div className="border border-[#10B981]/30 bg-gradient-to-br from-[#10B981]/8 via-zinc-950 to-zinc-950 rounded-sm p-5 flex items-center gap-5 flex-wrap" data-testid="gads-kpis">
      <Kpi label="Est. clicks/day" value={data.estimated_clicks_per_day} icon={MousePointer} />
      <Kpi label="Avg CPC" value={`$${data.estimated_cpc_usd}`} icon={DollarSign} />
      <Kpi label="Your budget" value={`$${budget}`} icon={DollarSign} />
      <Kpi label="Recommended" value={`$${data.recommended_budget_usd}`} icon={TrendingUp} />
      <div className="ml-auto">
        <Button onClick={onSave} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="gads-save-btn">
          <FileText className="w-4 h-4 mr-2" /> Save campaign
        </Button>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-zinc-500" />
      <div>
        <div className="text-[10px] tracking-widest uppercase text-zinc-500">{label}</div>
        <div className="font-display text-lg font-semibold">{value}</div>
      </div>
    </div>
  );
}

function AdCard({ ad, idx }) {
  const [copied, setCopied] = useState("");
  const copy = (val, key) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(""), 1200);
  };
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid={`gads-ad-${idx}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-medium">Ad #{idx + 1}</h3>
        <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500 text-[10px]">{ad.headlines.length} headlines · {ad.descriptions.length} descriptions</Badge>
      </div>
      <div className="space-y-3">
        <FieldList label="Headlines (≤30 chars)" items={ad.headlines} onCopy={copy} copiedKey={copied} prefix={`h-${idx}`} maxLen={30} />
        <FieldList label="Descriptions (≤90 chars)" items={ad.descriptions} onCopy={copy} copiedKey={copied} prefix={`d-${idx}`} maxLen={90} />
        <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t border-zinc-800">
          <FieldSingle label="Final URL" value={ad.final_url} onCopy={() => copy(ad.final_url, `u-${idx}`)} copied={copied === `u-${idx}`} />
          <FieldSingle label="Path 1" value={ad.path1} onCopy={() => copy(ad.path1, `p1-${idx}`)} copied={copied === `p1-${idx}`} />
          <FieldSingle label="Path 2" value={ad.path2} onCopy={() => copy(ad.path2, `p2-${idx}`)} copied={copied === `p2-${idx}`} />
        </div>
      </div>
    </div>
  );
}

function FieldList({ label, items, onCopy, copiedKey, prefix, maxLen }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-2">{label}</div>
      <ul className="grid sm:grid-cols-2 gap-1.5">
        {items.map((t, i) => {
          const k = `${prefix}-${i}`;
          const overflow = t.length > maxLen;
          return (
            <li key={i} className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm group">
              <span className="text-zinc-500 font-mono text-[10px] w-5">{(i + 1).toString().padStart(2, "0")}</span>
              <span className={`flex-1 truncate ${overflow ? "text-red-400" : "text-zinc-200"}`} title={t}>{t}</span>
              <span className="text-[10px] font-mono text-zinc-600 w-8 text-right">{t.length}</span>
              <button onClick={() => onCopy(t, k)} className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-zinc-200" data-testid={`copy-${k}`}>
                {copiedKey === k ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function FieldSingle({ label, value, onCopy, copied }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5">{label}</div>
      <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm">
        <span className="flex-1 truncate font-mono text-zinc-300" title={value}>{value || "—"}</span>
        <button onClick={onCopy} className="text-zinc-500 hover:text-zinc-200">
          {copied ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

function GroupCard({ group }) {
  const kws = group.keywords_enriched && group.keywords_enriched.length ? group.keywords_enriched : (group.keywords || []).map(k => ({ keyword: k }));
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-display font-medium">{group.name}</h3>
          <p className="text-xs text-zinc-500">{group.theme}</p>
        </div>
        <div className="flex gap-1.5">
          {(group.match_types || []).map(mt => <Badge key={mt} variant="outline" className="rounded-sm border-zinc-700 text-zinc-400 text-[10px] uppercase tracking-widest">{mt}</Badge>)}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
              <th className="pb-2 font-normal">Keyword</th>
              <th className="pb-2 font-normal text-right">Volume</th>
              <th className="pb-2 font-normal text-right">CPC</th>
              <th className="pb-2 font-normal text-right">Competition</th>
            </tr>
          </thead>
          <tbody>
            {kws.map((k, i) => (
              <tr key={i} className="border-b border-zinc-900 last:border-0">
                <td className="py-2 text-zinc-200">{k.keyword}</td>
                <td className="py-2 text-right text-zinc-300 font-mono">{(k.volume ?? 0).toLocaleString()}</td>
                <td className="py-2 text-right text-zinc-300 font-mono">${(k.cpc ?? 0).toFixed(2)}</td>
                <td className="py-2 text-right text-xs uppercase tracking-widest text-zinc-500">{k.competition_level || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NegativeList({ negatives }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <Ban className="w-4 h-4 text-zinc-500" />
        <h3 className="font-display font-medium">{negatives.length} negative keywords</h3>
        <span className="text-xs text-zinc-500 ml-2">Prevents wasted spend on irrelevant queries</span>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="gads-negatives">
        {negatives.map((n, i) => (
          <Badge key={i} variant="outline" className="rounded-sm border-zinc-700 text-zinc-300 text-xs">
            <ChevronRight className="w-3 h-3 mr-1 text-red-400" />
            {n}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function PushLiveButton({ campaignId, onPushed }) {
  const [busy, setBusy] = useState(false);
  const push = async () => {
    if (!window.confirm("Push this campaign to your linked Google Ads account?\n\nIt will be created in PAUSED state so you can review and resume it from Google Ads UI.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/google-ads/campaigns/${campaignId}/push-live`);
      toast.success(`Pushed live (PAUSED). Campaign ID: ${data.live_campaign_id}`);
      onPushed?.();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      const msg = formatApiError(e);
      if (code === "no_customer_linked" || code === "google_not_linked" || code === "ads_scope_missing") {
        toast.error(msg, { description: "Go to Integrations to connect your Google Ads account first.", duration: 6000 });
      } else if (code === "dev_token_not_approved") {
        toast.error("Google rejected the request because the developer token is not yet approved for production. If this is unexpected, contact support@myrankpilotai.com.", { duration: 8000 });
      } else {
        toast.error(msg, { duration: 6000 });
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button onClick={push} disabled={busy} size="sm"
            className="bg-[#10B981] hover:bg-[#0d8a64] rounded-sm text-white"
            data-testid={`gads-push-live-${campaignId}`}>
      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5 mr-1.5" />}
      Push live
    </Button>
  );
}

